// Copyright 2026 FlairCV Inc.
// SPDX-License-Identifier: LicenseRef-FlairCV-Enterprise

/**
 * Decides whether the hydration overlay should fire an automatic restore.
 *
 * Extracted from the component as a pure function for the same reason
 * deriveSyncBadge was: the bug is entirely in the predicate, and the repo has
 * no DOM test setup, so a plain vitest unit test needs neither.
 */

export interface HydrationAttemptState {
  /** The vault is unlocked and the account's rows have been purged. */
  needsHydration: boolean;
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
}

/**
 * Returns true when an automatic hydration attempt should be started.
 *
 * `isError` is the load-bearing term. Without it the overlay retried forever:
 * a failed attempt settles with isPending false and isSuccess false, which is
 * indistinguishable from "not started yet", so the effect fired again the
 * instant it finished — and again, and again, several hundred times a second,
 * each one a cloud download on the backend. The population this overlay exists
 * for (users returning after a purge, whose Drive we may well be unable to
 * reach) is exactly the population that hits the failing path.
 *
 * One automatic attempt, then stop and wait for the user. The overlay already
 * renders a Retry button for that, which is what the original intent must have
 * been — nothing else explains its presence.
 */
export function shouldAttemptHydration(state: HydrationAttemptState): boolean {
  return (
    state.needsHydration &&
    !state.isPending &&
    !state.isSuccess &&
    !state.isError
  );
}

/**
 * How the overlay should present a failed hydration.
 *
 * `retry` is a transient failure worth another go. `reconnect` is not: the
 * backend has told us it cannot read the user's cloud at all — either the blob
 * is gone from where we put it (`backup_missing`) or their OAuth token no
 * longer resolves to a provider (`storage_reconnect_required`).
 *
 * The distinction matters because the overlay's only affordance was a Retry
 * button. Offering it for these two puts the user in front of an empty account
 * pressing a button that cannot ever succeed — the backend has already set
 * needs_storage_reconnect and is waiting for them to reconnect storage, which
 * nothing on screen invited them to do.
 */
export type HydrationFailureKind = 'retry' | 'reconnect';

export function hydrationFailureKind(error: unknown): HydrationFailureKind {
  const code = (error as { code?: string } | null)?.code;
  if (code === 'backup_missing' || code === 'storage_reconnect_required') {
    return 'reconnect';
  }
  return 'retry';
}
